import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useForm,
  type DefaultValues,
  type FieldValues,
  type Path,
  type Resolver,
  type UseFormReturn
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ZodType } from 'zod';
import { useFormDialogWithUnsavedChanges } from '@bliks-io/ui';
import type {
  DuplicateCandidate,
  OrgFunctionSuggestionPayload,
  SystemSuggestionPayload
} from '@bliks-io/contracts';
import { useCreateEntitySuggestion } from '@/api/mutations/useCreateEntitySuggestion';
import { useLinkEntitySuggestion } from '@/api/mutations/useLinkEntitySuggestion';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useDuplicateCandidates } from '@/api/queries/useDuplicateCandidates';
import type { SuggestionPickEvent } from '../shared/suggestionPickEvent';

const DEBOUNCE_MS = 300;

/**
 * Correlates the suggestion kind with its payload mapper, so create requests
 * keep the contracts' discriminated union — no
 * `as CreateEntitySuggestionRequest` casts. Kept as a union (not independent
 * `kind` + `toApiPayload` fields) because TypeScript loses the correlation the
 * moment the two are typed separately.
 */
export type SuggestionKindArgs<TForm extends FieldValues> =
  | { kind: 'system'; toApiPayload: (form: TForm) => SystemSuggestionPayload }
  | {
      kind: 'orgFunction';
      toApiPayload: (form: TForm) => OrgFunctionSuggestionPayload;
    };

interface UseEntitySuggestionDialogBaseArgs<TForm extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Source-link: the source NODE's id — a step recording, or the start event
   * node the start-event picker (and node-anchored flows like
   * resolve-unclear) anchor to since issue 11.
   */
  sourceRecordingId?: string;
  /**
   * Process behind a node-anchored `sourceRecordingId` — forwarded to the
   * create mutation as a client-only cache-seed hint (never sent on the
   * wire) so the proposed-name chip appears immediately.
   */
  seedProcessId?: string;
  /**
   * Draft-editor origin: the create posts to `/entity-suggestion/draft/:id`
   * and `sourceRecordingId` names a draft node's `stableStepId`.
   */
  draftProcessId?: string;
  schema: ZodType<TForm>;
  defaultValues: DefaultValues<TForm>;
  /** Form field whose debounced value drives duplicate-candidate lookup. */
  primaryFieldName: Path<TForm>;
  /** Optional secondary field that refines the duplicate-candidate query. */
  secondaryFieldName?: Path<TForm>;
  /**
   * Map the secondary field's current value into the candidates query
   * filter — `category` for systems, `department` for teams.
   */
  buildCandidatesPayload: (args: { name: string; secondaryValue?: string }) => {
    name: string;
    category?: string;
    department?: string;
  };
  /** Called when the user picks an existing entity from the duplicate panel. */
  onUseExistingEntity?: (entityId: string) => void;
  /**
   * Fired after the suggestion is created (`kind: 'created'`) or a pending
   * duplicate is adopted (`kind: 'adopted'`), before the dialog closes.
   * Consumers use this to append `{ type: 'suggestion', id }` to the slot they
   * opened the dialog from — the contract requires the caller to write the
   * suggestion's id into the originating slot.
   */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
}

export type UseEntitySuggestionDialogArgs<TForm extends FieldValues> =
  UseEntitySuggestionDialogBaseArgs<TForm> & SuggestionKindArgs<TForm>;

export interface UseEntitySuggestionDialogResult<TForm extends FieldValues> {
  form: UseFormReturn<TForm>;
  candidates: DuplicateCandidate[];
  isCheckingDuplicates: boolean;
  isSubmitting: boolean;
  /** Create or adopt is in flight: the other action and closing wait. */
  busy: boolean;
  /** Pending suggestion whose adopt request is in flight. */
  adoptingId: string | undefined;
  handleFormSubmit: (form: TForm) => Promise<void>;
  handleUseExisting: (ref: { source: 'entity' | 'suggestion'; id: string }) => Promise<void>;
  showCloseConfirmation: boolean;
  handleOpenChange: (open: boolean) => void;
  handleDiscardChanges: () => void;
  handleStay: () => void;
}

/**
 * Shared mechanics for entity-suggestion creation dialogs (system + team):
 * form lifecycle, debounced duplicate-candidate lookup, create mutation on
 * submit, duplicate adoption, and unsaved-changes confirmation. Each kind plugs in its own
 * schema, payload shape, and field set.
 */
export function useEntitySuggestionDialog<TForm extends FieldValues>(
  args: UseEntitySuggestionDialogArgs<TForm>
): UseEntitySuggestionDialogResult<TForm> {
  // `kind` / `toApiPayload` stay on `args` — destructuring them would erase
  // the union correlation that types the create payload per kind.
  const {
    open,
    onOpenChange,
    sourceRecordingId,
    seedProcessId,
    draftProcessId,
    schema,
    defaultValues,
    primaryFieldName,
    secondaryFieldName,
    buildCandidatesPayload,
    onUseExistingEntity,
    onSuggestionCreated
  } = args;

  const form = useForm<TForm>({
    // The zodResolver overload signatures shifted between zod-v3 and the
    // current major; the cast keeps the hook generic regardless of which
    // resolver overload TypeScript picks for the workspace's installed zod.
    resolver: zodResolver(
      schema as unknown as Parameters<typeof zodResolver>[0]
    ) as unknown as Resolver<TForm>,
    defaultValues
  });

  const prevOpenRef = useRef(false);
  useEffect(
    function resetFormWhenDialogOpens() {
      if (open && !prevOpenRef.current) {
        form.reset(defaultValues);
      }
      prevOpenRef.current = open;
    },
    [open, defaultValues, form]
  );

  const watchedPrimary = form.watch(primaryFieldName) as string | undefined;
  const watchedSecondary = secondaryFieldName
    ? (form.watch(secondaryFieldName) as string | undefined)
    : undefined;

  const initialPrimary = (defaultValues as Record<string, unknown>)[primaryFieldName] ?? '';
  const [debouncedPrimary, setDebouncedPrimary] = useState<string>(
    typeof initialPrimary === 'string' ? initialPrimary : ''
  );
  const [debouncedSecondary, setDebouncedSecondary] = useState<string>('');

  useEffect(
    function debounceWatchedFields() {
      const timer = setTimeout(() => {
        setDebouncedPrimary(watchedPrimary ?? '');
        setDebouncedSecondary(watchedSecondary ?? '');
      }, DEBOUNCE_MS);
      return () => clearTimeout(timer);
    },
    [watchedPrimary, watchedSecondary]
  );

  const candidatesBody = useMemo(() => {
    const name = debouncedPrimary.trim();
    if (name.length < 3) return null;
    return {
      kind: args.kind,
      payload: buildCandidatesPayload({
        name,
        secondaryValue: debouncedSecondary.trim() || undefined
      })
    };
  }, [args.kind, debouncedPrimary, debouncedSecondary, buildCandidatesPayload]);

  const { data: candidates = [] as DuplicateCandidate[], isLoading: isCheckingDuplicates } =
    useDuplicateCandidates(candidatesBody);

  const createMutation = useCreateEntitySuggestion(draftProcessId);
  const linkMutation = useLinkEntitySuggestion();
  const busy = createMutation.isPending || linkMutation.isPending;

  const handleFormSubmit = async (formValues: TForm) => {
    if (busy) return;
    // The ternary narrows `args` per kind so the payload stays typed as the
    // matching contracts union member.
    const submission =
      args.kind === 'system'
        ? { kind: 'system' as const, payload: args.toApiPayload(formValues) }
        : { kind: 'orgFunction' as const, payload: args.toApiPayload(formValues) };
    if (!sourceRecordingId) {
      // Defensive: suggest-new is only offered when a source node exists
      // (`hasSource` gating in usePickerSlotSuggestions).
      throw new Error('entity-suggestion create requires a source node id');
    }
    const result = await createMutation.mutateAsync({
      ...submission,
      sourceRecordingId,
      seedProcessId,
      draftProcessId
    });
    // No success toast: the suggestion only reaches reviewers once the source
    // recording is submitted — "suggested" feedback here would mislead. The
    // proposed-name chip in the originating slot is the confirmation.
    onSuggestionCreated?.({ kind: 'created', suggestion: result.data });
    onOpenChange(false);
  };

  // "Use this instead" from the duplicate panel. An existing entity is handed
  // straight back to the caller. An existing *pending suggestion* is adopted
  // (the manual counterpart to the AI dedup's auto-link): maintain the
  // recording's entitySuggestions[] denorm server-side — a no-op for
  // start-event/process sources, which have no recording — then write the ref
  // into the originating slot via onSuggestionCreated. Emitted as `'adopted'`:
  // the suggestion existed before this dialog, so a withdrawing flow must
  // never treat it as its own create.
  const handleUseExisting = useCallback(
    async (ref: { source: 'entity' | 'suggestion'; id: string }) => {
      if (busy) return;
      if (ref.source === 'entity') {
        if (onUseExistingEntity) {
          onUseExistingEntity(ref.id);
          onOpenChange(false);
        }
        return;
      }
      if (sourceRecordingId) {
        try {
          await linkMutation.mutateAsync({
            suggestionId: ref.id,
            recordingId: sourceRecordingId,
            draftProcessId
          });
        } catch (error) {
          toastApiError(error, 'entitySuggestion.duplicates.linkError');
          return;
        }
      }
      onSuggestionCreated?.({ kind: 'adopted', suggestionId: ref.id });
      onOpenChange(false);
    },
    [
      busy,
      onUseExistingEntity,
      onOpenChange,
      sourceRecordingId,
      draftProcessId,
      linkMutation,
      onSuggestionCreated
    ]
  );

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);
  const {
    showCloseConfirmation,
    handleOpenChange: handleUnsavedOpenChange,
    handleDiscardChanges,
    handleStay
  } = useFormDialogWithUnsavedChanges({
    isDirty: form.formState.isDirty,
    onClose: handleClose
  });
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && busy) return;
      handleUnsavedOpenChange(nextOpen);
    },
    [busy, handleUnsavedOpenChange]
  );

  return {
    form,
    candidates,
    isCheckingDuplicates,
    isSubmitting: createMutation.isPending,
    busy,
    adoptingId: linkMutation.isPending ? linkMutation.variables?.suggestionId : undefined,
    handleFormSubmit,
    handleUseExisting,
    showCloseConfirmation,
    handleOpenChange,
    handleDiscardChanges,
    handleStay
  };
}
